<template>
	<div class="w-full bg-accented min-w-screen relative isolate overflow-hidden pb-4">
		<div class="text-center pt-16 text-5xl">
			<div class="font-semibold text-primary tracking-tight">
				{{ $t('testimonials.title1') }}
			</div>
			<div class="font-medium text-highlighted tracking-tight">
				{{ $t('testimonials.title2') }}
			</div>
		</div>
		<div class="w-full max-w-container mx-auto max-h-[850px] mask-linear-[0deg,transparent_10%,black,transparent_95%] px-4 sm:px-6 lg:px-8">
			<!-- Mobile: Single column -->
			<div class="block md:hidden">
				<UPageMarquee
					pause-on-hover
					reverse
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:90s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials"
						:id="`${TESTIMONIAL_CARD_ID}_${index}`"
						:key="index"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="testimonial.url"
						target="_blank"
						:ui="{ description: 'before:content-[open-quote] after:content-[close-quote] font-semibold' }"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
						@click="handleTestimonialClick($event, testimonial)"
						@mouseenter="handleTestimonialHover($event, testimonial)"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
			</div>

			<!-- Tablet: Two columns -->
			<div class="hidden md:block lg:hidden columns-2 gap-4">
				<UPageMarquee
					pause-on-hover
					reverse
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:50s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(0, 6)"
						:id="`${TESTIMONIAL_CARD_ID}_${index}`"
						:key="index"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="testimonial.url"
						target="_blank"
						:ui="{ description: 'before:content-[open-quote] after:content-[close-quote] font-semibold' }"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
						@click="handleTestimonialClick($event, testimonial)"
						@mouseenter="handleTestimonialHover($event, testimonial)"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
				<UPageMarquee
					pause-on-hover
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:60s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(6, 12)"
						:id="`${TESTIMONIAL_CARD_ID}_${index + 6}`"
						:key="index + 6"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="testimonial.url"
						target="_blank"
						:ui="{
							description: 'before:content-[open-quote] after:content-[close-quote] font-semibold'
						}"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
			</div>

			<!-- Desktop: Four columns -->
			<div class="hidden lg:block columns-3 xl:columns-4 gap-4">
				<UPageMarquee
					pause-on-hover
					reverse
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:30s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(0, 3)"
						:id="TESTIMONIAL_CARD_ID"
						:key="index"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="testimonial.url"
						target="_blank"
						:ui="{
							description: 'before:content-[open-quote] after:content-[close-quote] font-semibold'
						}"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
						@click="handleTestimonialClick($event, testimonial)"
						@mouseenter="handleTestimonialHover($event, testimonial)"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
				<UPageMarquee
					pause-on-hover
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:40s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(3, 6)"
						:id="TESTIMONIAL_CARD_ID"
						:key="index + 3"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="testimonial.url"
						target="_blank"
						:ui="{
							description: 'before:content-[open-quote] after:content-[close-quote] font-semibold'
						}"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
						@click="handleTestimonialClick($event, testimonial)"
						@mouseenter="handleTestimonialHover($event, testimonial)"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
				<UPageMarquee
					pause-on-hover
					reverse
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:45s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(6, 9)"
						:id="TESTIMONIAL_CARD_ID"
						:key="index + 6"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="testimonial.url"
						target="_blank"
						:ui="{ description: 'before:content-[open-quote] after:content-[close-quote] font-semibold' }"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
						@click="handleTestimonialClick($event, testimonial)"
						@mouseenter="handleTestimonialHover($event, testimonial)"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
				<UPageMarquee
					pause-on-hover
					:overlay="false"
					orientation="vertical"
					:ui="{ root: '[--gap:--spacing(4)] [--duration:50s]', content: 'w-auto py-1' }"
				>
					<UPageCard
						v-for="(testimonial, index) in testimonials.slice(9, 12)"
						:id="TESTIMONIAL_CARD_ID"
						:key="index + 9"
						variant="solid"
						:highlight="testimonial.highlight"
						:description="testimonial.quote"
						:to="testimonial.url"
						target="_blank"
						:ui="{ description: 'before:content-[open-quote] after:content-[close-quote] font-semibold' }"
						class="w-56 md:w-64 lg:w-72 shrink-0 cursor-pointer"
						@click="handleTestimonialClick($event, testimonial)"
						@mouseenter="handleTestimonialHover($event, testimonial)"
					>
						<template #header>
							<StarRating :rating="testimonial.rating" />
						</template>
						<template #footer>
							<UUser
								v-bind="testimonial.user"
								size="2xl"
								class="pt-1"
							/>
						</template>
					</UPageCard>
				</UPageMarquee>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { TESTIMONIALS } from '@/constants/components/testimonials'
import { TESTIMONIAL_CARD_ID } from '@/constants/ga4/tracking-ids'
import type { Testimonial } from '@/types/components/testimonial'

const props = defineProps({
	trackingLocation: {
		type: String,
		required: true
	}
})

const { trackEvent } = useGA4Tracking()

// Event handlers for testimonial card tracking
const handleTestimonialClick = (event: Event, testimonial: Testimonial) => {
	trackEvent(event, props.trackingLocation, {
		testimonial_url: testimonial.url,
		testimonial_rating: testimonial.rating
	})
}

const handleTestimonialHover = (event: Event, testimonial: Testimonial) => {
	trackEvent(event, props.trackingLocation, {
		testimonial_url: testimonial.url,
		testimonial_rating: testimonial.rating
	})
}

const testimonials = ref<Testimonial[]>([])
const intervalId = ref<NodeJS.Timeout>()
const previousIndex = ref<number | null>(null)

// Clear all highlights from testimonials
const clearAllHighlights = () => {
	testimonials.value.forEach((t: Testimonial) => t.highlight = false)
}

// Pick a new random testimonial to highlight (different from the previous one)
const cycleHighlight = () => {
	let index: number
	do {
		index = Math.floor(Math.random() * testimonials.value.length)
	} while (index === previousIndex.value)

	clearAllHighlights()

	// Set highlight
	const current = testimonials.value[index]
	if (current) {
		current.highlight = true
	}

	// store index to avoid repeating
	previousIndex.value = index
}

// Initialize testimonials
const initializeTestimonials = () => {
	testimonials.value = new Array(...TESTIMONIALS)

	// Initialize first highlighted testimonial
	cycleHighlight()
}

onMounted(() => {
	initializeTestimonials()
	intervalId.value = setInterval(cycleHighlight, 7000) // Change highlight every 7 seconds
})

onBeforeUnmount(() => {
	if (intervalId.value) {
		clearInterval(intervalId.value)
	}
})
</script>
